import { Box, Typography, useTheme } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import type { ReactNode } from "react";

import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";

export interface PMProgressBarProps extends Omit<PMBaseCardProps, "children"> {
  /** (string) Label to display on the left of the bar */
  label: string;
  /** (number) Percentage complete (0–100). Always required. */
  percent: number;
  /** (number | string) Completed value (optional, shown only if `total` is provided). Can be formatted string for locale support. */
  value?: number | string;
  /** (number | string) Total value (optional, shown only if `value` is provided). Can be formatted string for locale support. */
  total?: number | string;
  /** (ReactNode) Optional content shown above the progress bar. */
  topContent?: ReactNode;
  /** (ReactNode) Optional override for the right-side display text. */
  displayTextOverride?: ReactNode;
  /** (string) CSS color for the filled portion of the bar */
  barColor?: string;
  /** (string) CSS color for the track (unfilled portion) */
  trackColor?: string;
}

/**
 * PmProgressBar
 * A card component extending PMBaseCard that shows:
 * - A header (via PMBaseCard props)
 * - A row with label on the left and either "percent%" or "value/total (percent%)" on the right
 * - A horizontal progress bar driven by `percent`
 */
export const PMProgressBar: PimoReactComponent<PMProgressBarProps> = ({
  label,
  percent,
  value,
  total,
  topContent,
  displayTextOverride,
  barColor,
  trackColor,
  icon = "TrendingUp",
  title,
  showInfoIcon = false,
  showMenuIcon = false,
  ...rest
}) => {
  const theme = useTheme();

  // Determine display text: if both value+total exist, show ratio + percent
  const displayText =
    value !== undefined && total !== undefined
      ? `${value}/${total} (${percent}%)`
      : `${percent}%`;

  const fill = barColor ?? theme.palette.primary.light;
  const track = trackColor ?? theme.palette.grey[400];

  // Cap the visual progress bar at 100% while keeping the actual percent for display
  const cappedPercent = Math.min(percent, 100);

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      {...rest}
    >
      <Box sx={{ p: 2, pt: 0, pl: 0.3 }}>
        {topContent && (
          <Box sx={{ mt: -4, mb: 1, fontSize: "14px", lineHeight: "20px" }}>
            {topContent}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              textAlign: "left",
              width: "auto",
              lineHeight: 1.25,
            }}
          >
            {label}
          </Typography>
          <Typography variant="body2">
            {displayTextOverride ?? displayText}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={cappedPercent}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: track,
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              backgroundColor: fill,
            },
          }}
        />
      </Box>
    </PMBaseCard>
  );
};

export default PMProgressBar;
